import { useEffect, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";

import {
  roundAndDropTrailingZeros,
  rankPeriods,
  IRankListItem,
} from "@ascendnow/shared";

import AscGenericCard from "../AscGenericCard/AscGenericCard";
import AscAvatar from "../../AscAvatar/AscAvatar";
import {
  FirstPlaceBadge,
  SecondPlaceBadge,
  ThirdPlaceBadge,
} from "../../AscIllustrations";
import { SchoolRepresentativeIcon } from "../../AscIcons";
import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";
import AscButton from "../../AscButton/AscButton";
import AscDropdown from "../../AscDropdown/AscDropdown";
import AscLink from "../../AscLink/AscLink";
import styles from "./AscRanksCard.module.scss";

export interface AscRanksCardProps {
  title: string;
  students: IRankListItem[];
  className?: string;
  loading?: boolean;
  onClick?: () => void;
  showPeriod?: boolean;
  defaultPeriod?: (typeof rankPeriods)[number]["id"];
  onPeriodChange?: (period: (typeof rankPeriods)[number]["id"]) => void;
  seeMorePath?: string;
}

export default function AscRanksCard({
  title,
  students,
  className,
  loading = false,
  onClick,
  defaultPeriod = "week",
  showPeriod = false,
  onPeriodChange,
  seeMorePath,
}: AscRanksCardProps) {
  const [period, setPeriod] = useState<(typeof rankPeriods)[number]>();
  const placeIconHandler = (place: number) => {
    switch (place) {
      case 1:
        return <FirstPlaceBadge />;
      case 2:
        return <SecondPlaceBadge />;
      case 3:
        return <ThirdPlaceBadge />;
      default:
        return <span className={styles.place}>{place}.</span>;
    }
  };

  function handlePeriodChange(period: (typeof rankPeriods)[number]) {
    setPeriod(period);
    onPeriodChange?.(period?.id);
  }

  useEffect(() => {
    setPeriod(rankPeriods?.find((p) => p?.id === defaultPeriod));
  }, [defaultPeriod]);

  return (
    <AscGenericCard
      className={classNames(styles.ground, className)}
      titleClassName={styles.cardTitle}
      bodyClassName={styles.cardBody}
      contentClassName={styles.cardContent}
      title={
        <div className={styles.header}>
          <div className={styles.cardTitle}>{title}</div>
          {showPeriod ? (
            <AscDropdown title={period?.label}>
              {rankPeriods?.map((period) => (
                <AscDropdown.Option
                  key={period?.id}
                  onClick={() => handlePeriodChange(period)}
                >
                  {period?.label}
                </AscDropdown.Option>
              ))}
            </AscDropdown>
          ) : null}
        </div>
      }
      coverImageUrl={null}
      loading={loading}
      data-testid="asc-ranks-card"
      footer={
        <AscLink
          innerWrapperTag={null}
          href={seeMorePath + `?period=${period?.id}`}
        >
          <AscButton
            variant="secondary"
            fullWidth
            size="small"
            onClick={onClick}
            disabled={!students?.length}
          >
            See full list
          </AscButton>
        </AscLink>
      }
    >
      {students && Array.isArray(students) && students?.length > 0 ? (
        students?.map((student, index) => (
          <div key={student?.studentId} className={styles.studentItem}>
            <div className={styles.left}>
              {placeIconHandler(student.rank)}
              <AscAvatar
                src={student?.profilePicture?.location}
                flag={student?.flag}
              />
              <div className={styles.details}>
                <div className={styles.nameTag}>
                  <AscParagraph className={styles.name}>
                    {student?.name}
                  </AscParagraph>
                  {/* <AscTag label={student.tag} type="gold" /> */}
                </div>
                <div className={styles.ageSchool}>
                  {student.birthDate ? (
                    <>
                      <AscParagraph>
                        Age{" "}
                        {Math.floor(
                          dayjs().diff(dayjs(student?.birthDate), "year")
                        )}
                      </AscParagraph>
                      <AscParagraph>|</AscParagraph>
                    </>
                  ) : null}
                  <div className={styles.school}>
                    <SchoolRepresentativeIcon />
                    <AscParagraph>{student.school}</AscParagraph>
                  </div>
                </div>
              </div>
            </div>
            {student?.totalPoint && (
              <AscParagraph className={styles.score}>
                {roundAndDropTrailingZeros(Number(student?.totalPoint || 0))}
              </AscParagraph>
            )}
          </div>
        ))
      ) : (
        <div className={styles.noRanks}>No ranks yet</div>
      )}
    </AscGenericCard>
  );
}
